import { MapPinIcon } from "@heroicons/react/24/outline";
import {
    CalendarDate,
    CalendarEventId,
    CalendarEventType,
    CALENDAR_EVENT_TIME_GRANULARITY_MINUTES,
    DEFAULT_CALENDAR_EVENT_DURATION_MINUTES,
    formatDuration,
    genEntityId,
    getCurrentTimezone,
    ReferenceContactId,
    UserId,
} from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { DeleteButton } from "components/ui/action-button";
import {
    CommunityLink,
    ReferenceBusinessLink,
    ReferenceContactLink,
} from "components/ui/entity-link";
import { Input } from "components/ui/input";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { TextArea } from "components/ui/textarea";
import { produce } from "immer";
import React, { useCallback, useReducer } from "react";
import { useCurrentUserId } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { CalendarEventDto, VisitParent } from "./types";

type VisitEditorProps =
    | {
          mode: "edit";
          visit: CalendarEventDto;
          onSaved: () => void;
          onClose: () => void;
      }
    | {
          mode: "new";
          parent: VisitParent;
          onSaved: () => void;
          onClose: () => void;
      };

const DURATION_OPTIONS_MINUTES = [15, 30, 45, 60, 90, 120];

const buildDurationOptions = (currentMinutes: number): number[] => {
    if (DURATION_OPTIONS_MINUTES.includes(currentMinutes)) {
        return DURATION_OPTIONS_MINUTES;
    }
    return [...DURATION_OPTIONS_MINUTES, currentMinutes].sort((a, b) => a - b);
};

interface ComponentState {
    status: "loaded" | "saving" | "failed";
    visit: CalendarEventDto;
    visitExists: boolean;
    errorMessage: string;
}

type Action =
    | { type: "startSaving" }
    | { type: "setError"; message: string }
    | { type: "updateField"; field: keyof CalendarEventDto; value: string | null };

const defaultErrorMessage = "The visit failed to save.";

const reducer: React.Reducer<ComponentState, Action> = (p, a): ComponentState => {
    return produce(p, draft => {
        switch (a.type) {
            case "startSaving": {
                draft.status = "saving";
                draft.errorMessage = "";
                break;
            }
            case "setError": {
                draft.status = "failed";
                draft.errorMessage = a.message || defaultErrorMessage;
                break;
            }
            case "updateField": {
                if (a.field === "eventDuration") {
                    draft.visit.eventDuration = Number(a.value);
                } else {
                    (draft.visit as Record<string, unknown>)[a.field] = a.value;
                }
                break;
            }
        }
    });
};

const emptyVisit = (parent: VisitParent, userId: UserId): CalendarEventDto => {
    const now = new Date();
    const todayDate = CalendarDate.fromLocalDate(now);
    const base: CalendarEventDto = {
        id: genEntityId<CalendarEventId>(),
        parentId: parent.id,
        userId,
        type: CalendarEventType.Visit,
        eventDate: todayDate.toString(),
        eventTime: null,
        eventDuration: DEFAULT_CALENDAR_EVENT_DURATION_MINUTES,
        eventTimeZone: null,
        title: null,
        privateNotes: null,
        outcome: null,
        businessId: null,
        businessName: null,
        businessAddress: parent.address,
        communityId: null,
        communityName: null,
        communityAddress: null,
        parentContactName: null,
        dateAdded: now.toISOString(),
        updatedAt: now.toISOString(),
    };
    if (parent.kind === "contact") {
        return { ...base, parentContactName: parent.name };
    }
    return { ...base, businessId: parent.id, businessName: parent.name };
};

const initialStateForExisting = (visit: CalendarEventDto): ComponentState => {
    return {
        status: "loaded",
        visit,
        visitExists: true,
        errorMessage: "",
    };
};

const initialStateForNew = (parent: VisitParent, userId: UserId): ComponentState => {
    return {
        status: "loaded",
        visit: emptyVisit(parent, userId),
        visitExists: false,
        errorMessage: "",
    };
};

export const VisitEditor: React.FC<VisitEditorProps> = props => {
    const { onSaved, onClose } = props;
    const currentUserId = useCurrentUserId();
    const openedAsExisting = props.mode === "edit";

    const [state, dispatch] = useReducer(
        reducer,
        props.mode === "edit"
            ? initialStateForExisting(props.visit)
            : initialStateForNew(props.parent, currentUserId),
    );

    const currentDurationMinutes = state.visit.eventDuration;
    const durationOptions = buildDurationOptions(currentDurationMinutes);

    const trpcClient = trpc.useTRPC();

    const onSaveSuccess = useCallback(() => {
        onSaved();
        onClose();
    }, [onSaved, onClose]);

    const onSaveError = useCallback(
        (error: { message?: string }) => {
            dispatch({ type: "setError", message: error.message || defaultErrorMessage });
        },
        [dispatch],
    );

    const parentKind = state.visit.businessId ? "organization" : "contact";

    const onCreateSuccess = useCallback(() => {
        handleEvent("visit-save", { parentKind });
        onSaveSuccess();
    }, [onSaveSuccess, parentKind]);

    const onUpdateSuccess = useCallback(() => {
        if (state.visit.outcome?.trim()) {
            handleEvent("visit-outcome-recorded", { parentKind });
        }
        onSaveSuccess();
    }, [onSaveSuccess, parentKind, state.visit.outcome]);

    const createMutation = useMutation({
        ...trpcClient.calendarEvents.create.mutationOptions(),
        onSuccess: onCreateSuccess,
        onError: onSaveError,
    });

    const updateMutation = useMutation({
        ...trpcClient.calendarEvents.update.mutationOptions(),
        onSuccess: onUpdateSuccess,
        onError: onSaveError,
    });

    const deleteMutation = useMutation({
        ...trpcClient.calendarEvents.delete.mutationOptions(),
        onSuccess: () => {
            onSaved();
            onClose();
        },
        onError: onSaveError,
    });

    const handleFieldChange = useCallback(
        (field: keyof CalendarEventDto, value: string | null) => {
            dispatch({ type: "updateField", field, value });
        },
        [dispatch],
    );

    const handleSave = useCallback(() => {
        if (!state.visit.eventDate) {
            dispatch({
                type: "setError",
                message: "Please choose a date for the visit.",
            });
            return;
        }
        dispatch({ type: "startSaving" });
        const eventTimeZone = getCurrentTimezone();
        if (state.visitExists) {
            updateMutation.mutate({
                id: state.visit.id,
                userId: state.visit.userId,
                eventDate: state.visit.eventDate,
                eventTime: state.visit.eventTime,
                eventDuration: state.visit.eventDuration,
                eventTimeZone,
                title: state.visit.title,
                privateNotes: state.visit.privateNotes,
                outcome: state.visit.outcome,
            });
        } else {
            createMutation.mutate({
                parentId: state.visit.parentId,
                userId: state.visit.userId,
                eventDate: state.visit.eventDate,
                eventTime: state.visit.eventTime,
                eventDuration: state.visit.eventDuration,
                eventTimeZone,
                title: state.visit.title,
                privateNotes: state.visit.privateNotes,
            });
        }
    }, [state.visit, state.visitExists, createMutation, updateMutation]);

    const handleDelete = useCallback(() => {
        if (state.visitExists) {
            deleteMutation.mutate({ id: state.visit.id });
        }
    }, [state.visit.id, state.visitExists, deleteMutation]);

    const isSaving = state.status === "saving";

    return (
        <Panel type={PanelType.medium} headerText="" onDismiss={onClose} isOpen={true}>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium text-gray-900">
                        {state.visitExists ? "Edit Visit" : "Add Visit"}
                    </h2>
                    {state.visitExists ? (
                        <DeleteButton
                            backgroundColor="bg-white"
                            onClick={handleDelete}
                            confirm={{
                                title: "Delete Visit",
                                message: "Are you sure you want to delete this visit?",
                            }}
                        />
                    ) : null}
                </div>
                <div className="space-y-1">
                    {state.visit.parentContactName ? (
                        <div>
                            <ReferenceContactLink
                                referenceContactId={
                                    state.visit.parentId as ReferenceContactId
                                }
                                name={state.visit.parentContactName}
                            />
                        </div>
                    ) : null}
                    {state.visit.businessId && state.visit.businessName ? (
                        <div>
                            <ReferenceBusinessLink
                                referenceBusinessId={state.visit.businessId}
                                name={state.visit.businessName}
                            />
                        </div>
                    ) : null}
                    {state.visit.communityId && state.visit.communityName ? (
                        <div>
                            <CommunityLink
                                communityId={state.visit.communityId}
                                name={state.visit.communityName}
                            />
                        </div>
                    ) : null}
                    {(state.visit.businessAddress ?? state.visit.communityAddress) ? (
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                            <MapPinIcon
                                className="w-4 h-4 flex-shrink-0"
                                aria-hidden="true"
                            />
                            <span>
                                {state.visit.businessAddress ??
                                    state.visit.communityAddress}
                            </span>
                        </div>
                    ) : null}
                </div>
                <Input
                    label="Purpose"
                    value={state.visit.title || ""}
                    onChange={e => handleFieldChange("title", e.target.value || null)}
                />

                <div>
                    <label className="block mb-1">Date</label>
                    <input
                        type="date"
                        required
                        value={state.visit.eventDate || ""}
                        onChange={e => {
                            if (e.target.value) {
                                handleFieldChange("eventDate", e.target.value);
                            }
                        }}
                        className="form-input rounded-sm md:rounded w-full disabled:bg-gray-100 border-gray-300 hover:border-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300"
                    />
                </div>

                <div>
                    <label className="block mb-1">Time (optional)</label>
                    <input
                        type="time"
                        step={CALENDAR_EVENT_TIME_GRANULARITY_MINUTES * 60}
                        value={state.visit.eventTime || ""}
                        onChange={e =>
                            handleFieldChange("eventTime", e.target.value || null)
                        }
                        className="form-input rounded-sm md:rounded w-full disabled:bg-gray-100 border-gray-300 hover:border-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300"
                    />
                </div>

                <div>
                    <Select
                        label="Duration"
                        disabled={!state.visit.eventTime}
                        value={String(currentDurationMinutes)}
                        onChange={e =>
                            handleFieldChange("eventDuration", e.target.value)
                        }>
                        {durationOptions.map(minutes => (
                            <option key={minutes} value={String(minutes)}>
                                {formatDuration(minutes)}
                            </option>
                        ))}
                    </Select>
                </div>

                <UserSelect
                    label="Assigned to"
                    value={state.visit.userId as SelectableUser}
                    includeEveryone={false}
                    onChange={userId => {
                        if (userId) {
                            handleFieldChange("userId", userId as string);
                        }
                    }}
                />

                <TextArea
                    label="Internal Notes"
                    rows={3}
                    value={state.visit.privateNotes || ""}
                    onChange={e =>
                        handleFieldChange("privateNotes", e.target.value || null)
                    }
                />

                {openedAsExisting ? (
                    <TextArea
                        label="Outcome"
                        rows={3}
                        value={state.visit.outcome || ""}
                        placeholder="Recap of what happened during the visit"
                        onChange={e =>
                            handleFieldChange("outcome", e.target.value || null)
                        }
                    />
                ) : null}

                {state.errorMessage ? (
                    <div className="text-red-600 text-sm">{state.errorMessage}</div>
                ) : null}

                <div className="flex justify-end items-center gap-2 pt-4">
                    {isSaving ? <Spinner /> : null}
                    <SecondaryButton onClick={onClose} disabled={isSaving}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton onClick={handleSave} disabled={isSaving}>
                        Save
                    </PrimaryButton>
                </div>
            </div>
        </Panel>
    );
};
